import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  Calendar, Users, Clock, RefreshCw, ChevronLeft, Plus,
  UserCheck, Activity, Brain, AlertTriangle, CheckCircle,
  CalendarDays, Building, Loader2, Stethoscope, Heart,
  Briefcase, User, Phone, Mail, Shield, ArrowRight,
  BarChart3, AlertCircle, Edit, Trash2, Eye
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// IDG Role configurations with icons
const ROLE_ICONS = {
  physician: Stethoscope,
  medical_director: Shield,
  rn: Heart,
  lpn: Heart,
  social_worker: Users,
  chaplain: Heart,
  aide: UserCheck,
  bereavement_coordinator: Heart,
  volunteer_coordinator: Users,
  volunteer: User,
  pt: Activity,
  ot: Activity,
  st: Activity,
  dietitian: Briefcase,
  np: Stethoscope,
  pharmacist: Briefcase
};

export default function SchedulingManagement() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [idgRoles, setIdgRoles] = useState({});
  const [shiftTypes, setShiftTypes] = useState({});
  const [dashboard, setDashboard] = useState(null);
  const [staff, setStaff] = useState([]);
  const [weekSchedule, setWeekSchedule] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [weekStartDate, setWeekStartDate] = useState(getMonday(new Date()).toISOString().split('T')[0]);
  
  // Dialogs
  const [addStaffDialog, setAddStaffDialog] = useState(false);
  const [assignShiftDialog, setAssignShiftDialog] = useState(false);
  const [optimizeDialog, setOptimizeDialog] = useState(false);
  const [interventionDialog, setInterventionDialog] = useState(false);
  
  // Forms
  const [newStaff, setNewStaff] = useState({
    name: "", email: "", phone: "", role: "rn", shift_preference: "day",
    license_number: "", max_daily_visits: 8, max_weekly_hours: 40
  });
  const [newShift, setNewShift] = useState({
    staff_id: "", date: selectedDate, shift_type: "day"
  });
  const [optimizeParams, setOptimizeParams] = useState({
    date_range_start: selectedDate,
    date_range_end: addDays(selectedDate, 7),
    consider_acuity: true,
    balance_workload: true,
    minimize_travel: true
  });
  const [optimizeResults, setOptimizeResults] = useState(null);
  
  const token = localStorage.getItem("hospice_token");

  // Helper functions
  function getMonday(d) {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.setDate(diff));
  }
  
  function addDays(dateStr, days) {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  }

  const fetchIdgRoles = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/scheduling/idg-roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIdgRoles(data.roles || {});
        setShiftTypes(data.shift_types || {});
      }
    } catch (error) {
      console.error("Error fetching IDG roles:", error);
    }
  }, [token]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/scheduling/dashboard/comprehensive`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    }
  }, [token]);

  const fetchStaff = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/scheduling/staff`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStaff(data.staff || []);
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  }, [token]);

  const fetchWeekSchedule = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/scheduling/shifts/weekly?start_date=${weekStartDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWeekSchedule(data);
      }
    } catch (error) {
      console.error("Error fetching week schedule:", error);
    }
  }, [token, weekStartDate]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchIdgRoles(), fetchDashboard(), fetchStaff(), fetchWeekSchedule()]);
    setLoading(false);
  }, [fetchIdgRoles, fetchDashboard, fetchStaff, fetchWeekSchedule]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success("Dashboard refreshed");
  };

  const handleAddStaff = async () => {
    try {
      const roleInfo = idgRoles[newStaff.role] || {};
      const staffData = {
        ...newStaff,
        role_title: roleInfo.title || newStaff.role
      };
      
      const res = await fetch(`${API_URL}/api/ehr/scheduling/staff/register`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(staffData)
      });
      
      if (res.ok) {
        toast.success("Staff member registered successfully");
        setAddStaffDialog(false);
        setNewStaff({ name: "", email: "", phone: "", role: "rn", shift_preference: "day", license_number: "", max_daily_visits: 8, max_weekly_hours: 40 });
        fetchStaff();
        fetchDashboard();
      } else {
        toast.error("Failed to register staff member");
      }
    } catch (error) {
      toast.error("Error adding staff");
    }
  };

  const handleAssignShift = async () => {
    try {
      const staffMember = staff.find(s => s.id === newShift.staff_id);
      if (!staffMember) {
        toast.error("Please select a staff member");
        return;
      }
      
      const shiftData = {
        staff_id: newShift.staff_id,
        staff_name: staffMember.name,
        staff_role: staffMember.role,
        shift_type: newShift.shift_type,
        date: newShift.date,
        start_time: shiftTypes[newShift.shift_type]?.start || "08:00",
        end_time: shiftTypes[newShift.shift_type]?.end || "17:00"
      };
      
      const res = await fetch(`${API_URL}/api/ehr/scheduling/shifts/assign`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(shiftData)
      });
      
      if (res.ok) {
        toast.success("Shift assigned successfully");
        setAssignShiftDialog(false);
        fetchWeekSchedule();
        fetchDashboard();
      } else {
        toast.error("Failed to assign shift");
      }
    } catch (error) {
      toast.error("Error assigning shift");
    }
  };

  const handleOptimizeSchedule = async () => {
    try {
      setRefreshing(true);
      const res = await fetch(`${API_URL}/api/ehr/scheduling/optimize`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(optimizeParams)
      });
      
      if (res.ok) {
        const data = await res.json();
        setOptimizeResults(data);
        toast.success(`Generated ${data.total_recommendations} AI recommendations`);
      } else {
        toast.error("Optimization failed");
      }
    } catch (error) {
      toast.error("Error optimizing schedule");
    } finally {
      setRefreshing(false);
    }
  };

  const handleApplyOptimization = async () => {
    if (!optimizeResults?.recommendations?.length) {
      toast.error("No recommendations to apply");
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/ehr/scheduling/optimize/apply`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(optimizeResults.recommendations)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Created ${data.slots_created} schedule slots`);
        setOptimizeDialog(false);
        setOptimizeResults(null);
        fetchDashboard();
      } else {
        toast.error("Failed to apply optimization");
      }
    } catch (error) {
      toast.error("Error applying optimization");
    }
  };

  const handleGenerateWeekShifts = async () => {
    try {
      setRefreshing(true);
      const res = await fetch(`${API_URL}/api/ehr/scheduling/shifts/generate-week?start_date=${weekStartDate}&include_weekends=true`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Generated ${data.assignments_created} shift assignments`);
        fetchWeekSchedule();
        fetchDashboard();
      } else {
        toast.error("Failed to generate shifts");
      }
    } catch (error) {
      toast.error("Error generating shifts");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
      </div>
    );
  }

  const summary = dashboard?.summary || {};
  const idgCoverage = dashboard?.idg_coverage || [];

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="scheduling-management">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => window.location.href = '/hospice-ehr/command-center'}
                className="text-slate-400 hover:text-white"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Calendar className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Staff Scheduling</h1>
                <p className="text-slate-400 text-sm">IDG-based automated scheduling</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefresh}
                disabled={refreshing}
                className="border-slate-600 text-slate-300"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                onClick={() => setOptimizeDialog(true)}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              >
                <Brain className="w-4 h-4 mr-2" />
                AI Optimize
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <StatCard icon={<CalendarDays className="w-5 h-5" />} label="Today's Visits" value={summary.today_visits || 0} color="cyan" />
          <StatCard icon={<CheckCircle className="w-5 h-5" />} label="Completed" value={summary.today_completed || 0} color="green" />
          <StatCard icon={<CalendarDays className="w-5 h-5" />} label="Tomorrow" value={summary.tomorrow_visits || 0} color="blue" />
          <StatCard icon={<Users className="w-5 h-5" />} label="Total Staff" value={summary.total_staff || 0} color="purple" />
          <StatCard icon={<Activity className="w-5 h-5" />} label="On Shift Today" value={summary.today_on_shift || 0} color="emerald" />
          <StatCard icon={<AlertTriangle className="w-5 h-5" />} label="Pending Actions" value={summary.pending_interventions || 0} color="amber" critical={summary.critical_interventions || 0} />
        </div>

        {/* IDG Coverage Alert */}
        {idgCoverage.some(r => !r.covered) && (
          <div className="mb-6 bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-400" />
              <div>
                <p className="font-semibold text-red-200">IDG Coverage Gaps Detected</p>
                <p className="text-sm text-red-300/70">
                  Missing: {idgCoverage.filter(r => !r.covered).map(r => r.title).join(", ")}
                </p>
              </div>
              <Button size="sm" className="ml-auto bg-red-600 hover:bg-red-700" onClick={() => setAssignShiftDialog(true)}>
                Assign Coverage
              </Button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-slate-800 border border-slate-700 w-full justify-start mb-4">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-300">
              <BarChart3 className="w-4 h-4 mr-2" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="staff" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-300">
              <Users className="w-4 h-4 mr-2" /> IDG Staff
            </TabsTrigger>
            <TabsTrigger value="shifts" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
              <Calendar className="w-4 h-4 mr-2" /> Weekly Shifts
            </TabsTrigger>
            <TabsTrigger value="visits" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-300">
              <CalendarDays className="w-4 h-4 mr-2" /> Visit Schedule
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* IDG Coverage */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-cyan-400" />
                    IDG Coverage Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {idgCoverage.map((role, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-slate-700/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          {role.covered ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-red-400" />
                          )}
                          <span className="text-slate-300 text-sm">{role.title}</span>
                        </div>
                        <Badge className={role.covered ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}>
                          {role.on_shift} on shift
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Staff by Role */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-400" />
                    Staff Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(dashboard?.staff_by_role || {}).map(([roleKey, count]) => {
                      const roleInfo = idgRoles[roleKey] || {};
                      return (
                        <div key={roleKey} className="flex items-center justify-between p-2 bg-slate-700/50 rounded-lg">
                          <span className="text-slate-300 text-sm">{roleInfo.title || roleKey}</span>
                          <Badge style={{ backgroundColor: `${roleInfo.color}30`, color: roleInfo.color }}>
                            {count}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                  <Button 
                    className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
                    onClick={() => setAddStaffDialog(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Staff Member
                  </Button>
                </CardContent>
              </Card>

              {/* Today's Status */}
              <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-emerald-400" />
                    Today&apos;s Schedule Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-blue-400">{dashboard?.today_by_status?.scheduled || 0}</div>
                      <p className="text-sm text-slate-400">Scheduled</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-yellow-400">{dashboard?.today_by_status?.in_progress || 0}</div>
                      <p className="text-sm text-slate-400">In Progress</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-green-400">{dashboard?.today_by_status?.completed || 0}</div>
                      <p className="text-sm text-slate-400">Completed</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-red-400">{dashboard?.today_by_status?.missed || 0}</div>
                      <p className="text-sm text-slate-400">Missed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Staff Tab */}
          <TabsContent value="staff">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-white">IDG Staff Directory</CardTitle>
                <Button onClick={() => setAddStaffDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" /> Add Staff
                </Button>
              </CardHeader>
              <CardContent>
                {staff.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No staff members registered</p>
                    <Button className="mt-4 bg-emerald-600" onClick={() => setAddStaffDialog(true)}>
                      Add First Staff Member
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {staff.map((member) => {
                      const roleInfo = idgRoles[member.role] || {};
                      const RoleIcon = ROLE_ICONS[member.role] || User;
                      return (
                        <div 
                          key={member.id} 
                          className="bg-slate-700/50 rounded-lg p-4 border border-slate-600"
                        >
                          <div className="flex items-start gap-3">
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: `${roleInfo.color}30` }}
                            >
                              <RoleIcon className="w-5 h-5" style={{ color: roleInfo.color }} />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-white">{member.name}</h4>
                              <p className="text-sm text-slate-400">{roleInfo.title || member.role}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge className="text-xs" style={{ backgroundColor: `${roleInfo.color}30`, color: roleInfo.color }}>
                                  {roleInfo.code || member.role.toUpperCase()}
                                </Badge>
                                <Badge className="bg-slate-600 text-slate-300 text-xs">
                                  {member.shift_preference || "day"} shift
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" /> {member.email}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Weekly Shifts Tab */}
          <TabsContent value="shifts">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-white">Weekly Shift Schedule</CardTitle>
                  <p className="text-sm text-slate-400">Week of {weekStartDate}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input 
                    type="date" 
                    value={weekStartDate}
                    onChange={(e) => setWeekStartDate(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white w-40"
                  />
                  <Button onClick={handleGenerateWeekShifts} disabled={refreshing} className="bg-cyan-600 hover:bg-cyan-700">
                    <Brain className="w-4 h-4 mr-2" />
                    Auto-Generate
                  </Button>
                  <Button onClick={() => setAssignShiftDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4 mr-2" /> Assign Shift
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {!weekSchedule || weekSchedule.schedule?.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No shifts scheduled for this week</p>
                    <Button className="mt-4 bg-cyan-600" onClick={handleGenerateWeekShifts}>
                      Generate Week Schedule
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {weekSchedule.week_dates?.map((date) => (
                      <div key={date} className="bg-slate-700/30 rounded-lg p-4">
                        <h4 className="font-semibold text-white mb-3">
                          {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {weekSchedule.by_day?.[date]?.map((shift, i) => {
                            const roleInfo = idgRoles[shift.staff_role] || {};
                            return (
                              <div key={i} className="bg-slate-700/50 rounded-lg p-2 text-sm">
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: roleInfo.color || '#64748b' }}
                                  />
                                  <span className="text-white font-medium truncate">{shift.staff_name}</span>
                                </div>
                                <div className="text-slate-400 text-xs mt-1">
                                  {roleInfo.code || shift.staff_role} • {shift.start_time} - {shift.end_time}
                                </div>
                              </div>
                            );
                          })}
                          {(!weekSchedule.by_day?.[date] || weekSchedule.by_day[date].length === 0) && (
                            <div className="col-span-4 text-center text-slate-500 py-2 text-sm">
                              No shifts assigned
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Visits Tab */}
          <TabsContent value="visits">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-white">Patient Visit Schedule</CardTitle>
                  <p className="text-sm text-slate-400">This week: {summary.week_total_visits || 0} visits</p>
                </div>
                <Button onClick={() => setOptimizeDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Brain className="w-4 h-4 mr-2" /> AI Schedule Optimizer
                </Button>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-slate-400">
                  <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Use AI Optimizer to generate patient visit schedules</p>
                  <p className="text-sm mt-2">Based on patient acuity, staff availability, and travel optimization</p>
                  <Button className="mt-4 bg-purple-600" onClick={() => setOptimizeDialog(true)}>
                    <Brain className="w-4 h-4 mr-2" /> Open AI Optimizer
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Staff Dialog */}
      <Dialog open={addStaffDialog} onOpenChange={setAddStaffDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Add IDG Staff Member</DialogTitle>
            <DialogDescription className="text-slate-400">
              Register a new staff member with their IDG role
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Full Name *</Label>
              <Input 
                value={newStaff.name}
                onChange={(e) => setNewStaff({...newStaff, name: e.target.value})}
                placeholder="Dr. Jane Smith"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email *</Label>
                <Input 
                  type="email"
                  value={newStaff.email}
                  onChange={(e) => setNewStaff({...newStaff, email: e.target.value})}
                  placeholder="jane@hospice.com"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  value={newStaff.phone}
                  onChange={(e) => setNewStaff({...newStaff, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>IDG Role *</Label>
                <Select value={newStaff.role} onValueChange={(v) => setNewStaff({...newStaff, role: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {Object.entries(idgRoles).map(([key, role]) => (
                      <SelectItem key={key} value={key} className="text-white hover:bg-slate-600">
                        {role.title} ({role.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Shift Preference</Label>
                <Select value={newStaff.shift_preference} onValueChange={(v) => setNewStaff({...newStaff, shift_preference: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {Object.entries(shiftTypes).map(([key, shift]) => (
                      <SelectItem key={key} value={key} className="text-white hover:bg-slate-600">
                        {shift.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>License Number</Label>
                <Input 
                  value={newStaff.license_number}
                  onChange={(e) => setNewStaff({...newStaff, license_number: e.target.value})}
                  placeholder="RN12345"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Max Daily Visits</Label>
                <Input 
                  type="number"
                  value={newStaff.max_daily_visits}
                  onChange={(e) => setNewStaff({...newStaff, max_daily_visits: parseInt(e.target.value)})}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddStaffDialog(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleAddStaff} className="bg-emerald-600 hover:bg-emerald-700">
              Add Staff Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Shift Dialog */}
      <Dialog open={assignShiftDialog} onOpenChange={setAssignShiftDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Assign Shift</DialogTitle>
            <DialogDescription className="text-slate-400">
              Assign a staff member to a shift
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Staff Member *</Label>
              <Select value={newShift.staff_id} onValueChange={(v) => setNewShift({...newShift, staff_id: v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  {staff.map((s) => (
                    <SelectItem key={s.id} value={s.id} className="text-white hover:bg-slate-600">
                      {s.name} - {idgRoles[s.role]?.title || s.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input 
                  type="date"
                  value={newShift.date}
                  onChange={(e) => setNewShift({...newShift, date: e.target.value})}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Shift Type *</Label>
                <Select value={newShift.shift_type} onValueChange={(v) => setNewShift({...newShift, shift_type: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {Object.entries(shiftTypes).map(([key, shift]) => (
                      <SelectItem key={key} value={key} className="text-white hover:bg-slate-600">
                        {shift.name} ({shift.start} - {shift.end})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignShiftDialog(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleAssignShift} className="bg-cyan-600 hover:bg-cyan-700">
              Assign Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Optimize Dialog */}
      <Dialog open={optimizeDialog} onOpenChange={setOptimizeDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400" />
              AI Schedule Optimizer
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Generate optimized visit schedules based on patient acuity and staff availability
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input 
                  type="date"
                  value={optimizeParams.date_range_start}
                  onChange={(e) => setOptimizeParams({...optimizeParams, date_range_start: e.target.value})}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input 
                  type="date"
                  value={optimizeParams.date_range_end}
                  onChange={(e) => setOptimizeParams({...optimizeParams, date_range_end: e.target.value})}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge 
                className={`cursor-pointer ${optimizeParams.consider_acuity ? 'bg-purple-500/30 text-purple-300' : 'bg-slate-600 text-slate-400'}`}
                onClick={() => setOptimizeParams({...optimizeParams, consider_acuity: !optimizeParams.consider_acuity})}
              >
                Consider Acuity {optimizeParams.consider_acuity && '✓'}
              </Badge>
              <Badge 
                className={`cursor-pointer ${optimizeParams.balance_workload ? 'bg-cyan-500/30 text-cyan-300' : 'bg-slate-600 text-slate-400'}`}
                onClick={() => setOptimizeParams({...optimizeParams, balance_workload: !optimizeParams.balance_workload})}
              >
                Balance Workload {optimizeParams.balance_workload && '✓'}
              </Badge>
              <Badge 
                className={`cursor-pointer ${optimizeParams.minimize_travel ? 'bg-emerald-500/30 text-emerald-300' : 'bg-slate-600 text-slate-400'}`}
                onClick={() => setOptimizeParams({...optimizeParams, minimize_travel: !optimizeParams.minimize_travel})}
              >
                Minimize Travel {optimizeParams.minimize_travel && '✓'}
              </Badge>
            </div>

            {/* Results */}
            {optimizeResults && (
              <div className="mt-4 bg-slate-700/50 rounded-lg p-4">
                <h4 className="font-semibold text-white mb-3">
                  AI Recommendations ({optimizeResults.total_recommendations})
                </h4>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-slate-600/50 rounded p-2 text-center">
                    <div className="text-lg font-bold text-cyan-400">{optimizeResults.patients_analyzed}</div>
                    <div className="text-xs text-slate-400">Patients</div>
                  </div>
                  <div className="bg-slate-600/50 rounded p-2 text-center">
                    <div className="text-lg font-bold text-purple-400">{optimizeResults.staff_available}</div>
                    <div className="text-xs text-slate-400">Staff</div>
                  </div>
                  <div className="bg-slate-600/50 rounded p-2 text-center">
                    <div className="text-lg font-bold text-emerald-400">{optimizeResults.total_recommendations}</div>
                    <div className="text-xs text-slate-400">Visits</div>
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {optimizeResults.recommendations?.slice(0, 10).map((rec, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-600/30 rounded p-2 text-sm">
                      <div>
                        <span className="text-white">{rec.patient_name}</span>
                        <span className="text-slate-400 ml-2">→ {rec.staff_name}</span>
                      </div>
                      <Badge className={
                        rec.priority === 'critical' ? 'bg-red-500/30 text-red-300' :
                        rec.priority === 'high' ? 'bg-orange-500/30 text-orange-300' :
                        'bg-cyan-500/30 text-cyan-300'
                      }>
                        {rec.acuity_level}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOptimizeDialog(false); setOptimizeResults(null); }} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            {!optimizeResults ? (
              <Button onClick={handleOptimizeSchedule} disabled={refreshing} className="bg-purple-600 hover:bg-purple-700">
                {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                Generate Recommendations
              </Button>
            ) : (
              <Button onClick={handleApplyOptimization} className="bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle className="w-4 h-4 mr-2" />
                Apply {optimizeResults.total_recommendations} Recommendations
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, value, color, critical }) {
  const colorClasses = {
    cyan: "from-cyan-500/20 to-cyan-600/10 border-cyan-500/30 text-cyan-400",
    green: "from-green-500/20 to-green-600/10 border-green-500/30 text-green-400",
    blue: "from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400",
    purple: "from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400",
    emerald: "from-emerald-500/20 to-emerald-600/10 border-emerald-500/30 text-emerald-400",
    amber: "from-amber-500/20 to-amber-600/10 border-amber-500/30 text-amber-400"
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} border rounded-xl p-4`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-3xl font-bold">{value}</div>
      {critical > 0 && (
        <div className="text-xs text-red-400 mt-1">{critical} critical</div>
      )}
    </div>
  );
}
